import type Alpine from "alpinejs";
import { isDialogActive, isInDialog } from "../context";

/**
 * Listen to keydown event to fire mousedown/mouseup events.
 * @param el Element that will received the keydown event
 * @param directive The modifier will be the key to listen on. The expression will be the css selector to fire mousedown/mouseup events.
 * @param utilities Standard Alpine utilities for cleanup.
 */
const shortcut: Alpine.DirectiveCallback = (el, directive, utilities) => {
  const modifiers = directive.modifiers;

  const modifierKeys = ["ctrl", "control", "shift", "alt", "meta", "cmd"];
  const isModifierKey = (m: string) => modifierKeys.includes(m.toLowerCase());

  // 1. Identify key, scope, modifiers, and extra events
  let scope: any = document;
  let keyToListen: string | undefined;
  const extraEvents: string[] = [];

  const expectedModifiers = {
    ctrl: modifiers.includes("ctrl") || modifiers.includes("control"),
    shift: modifiers.includes("shift"),
    alt: modifiers.includes("alt"),
    meta: modifiers.includes("meta") || modifiers.includes("cmd"),
  };

  const isDialogScoped = isInDialog(el);

  for (const m of modifiers) {
    const lower = m.toLowerCase();

    if (["el", "document"].includes(lower)) {
      if (lower === "el") scope = el;
      if (lower === "document") scope = document;
      continue;
    } else if (isModifierKey(lower)) {
      // already handled in expectedModifiers
      continue;
    } else if (!keyToListen) {
      keyToListen = lower;
    } else {
      extraEvents.push(lower);
    }
  }

  // 2. Determine the selector and final key
  let selector = directive.expression;
  if (!keyToListen && selector) {
    // Case: x-shortcut="Enter" -> Enter is the key, target is el.
    keyToListen = selector.toLowerCase();
    selector = "";
  }

  if (!keyToListen) return;

  // 3. Determine events to fire
  let downEvent: string | null = null;
  let upEvent: string | null = null;

  if (extraEvents.length === 0) {
    downEvent = "mousedown";
    upEvent = "mouseup";
  } else if (extraEvents.length === 1) {
    const event = extraEvents[0];
    // If only one event, decide when to fire based on common patterns
    if (["mousedown", "keydown", "focus", "focusin"].includes(event)) {
      downEvent = event;
    } else {
      upEvent = event;
    }
  } else {
    downEvent = extraEvents[0];
    upEvent = extraEvents[1];
  }

  // State to track if this specific shortcut instance "claimed" the current key press
  let isActive = false;

  function isDisabled(el: Element) {
    const value = el.getAttribute("disabled");
    return value !== null && value !== "false";
  }

  function matchesModifiers(e: KeyboardEvent) {
    return (
      e.ctrlKey === expectedModifiers.ctrl &&
      e.shiftKey === expectedModifiers.shift &&
      e.altKey === expectedModifiers.alt &&
      e.metaKey === expectedModifiers.meta
    );
  }

  function fireEvent(target: Element, eventName: string) {
    if (["mousedown", "mouseup", "click", "dblclick", "pointerdown", "pointerup"].includes(eventName)) {
      target.dispatchEvent(new MouseEvent(eventName, { bubbles: true, cancelable: true }));
    } else {
      target.dispatchEvent(new CustomEvent(eventName, { bubbles: true, cancelable: true }));
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key.toLowerCase() !== keyToListen) return;
    if (isDialogScoped !== isDialogActive()) return;

    if (matchesModifiers(e)) {
      isActive = true;
      if (downEvent) {
        const targets = selector ? scope.querySelectorAll(selector) : [el];
        let fired = false;
        for (let target of targets) {
          if (isDisabled(target)) continue;
          fireEvent(target, downEvent);
          fired = true;
        }
        if (fired) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }
    } else {
      // If it was active but modifiers no longer match (shouldn't happen on keydown but for safety)
      isActive = false;
    }
  }

  function onKeyUp(e: KeyboardEvent) {
    if (e.key.toLowerCase() !== keyToListen) return;
    if (isDialogScoped !== isDialogActive()) return;

    if (isActive) {
      isActive = false;
      if (upEvent) {
        const targets = selector ? scope.querySelectorAll(selector) : [el];
        let fired = false;
        for (let target of targets) {
          if (isDisabled(target)) continue;
          fireEvent(target, upEvent);
          fired = true;
        }
        if (fired) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }
    }
  }

  // Safety: reset state if window loses focus
  function onBlur() {
    isActive = false;
  }

  scope.addEventListener("keydown", onKeyDown);
  scope.addEventListener("keyup", onKeyUp);
  window.addEventListener("blur", onBlur);

  utilities.cleanup(() => {
    scope.removeEventListener("keydown", onKeyDown);
    scope.removeEventListener("keyup", onKeyUp);
    window.removeEventListener("blur", onBlur);
  });
};

export default shortcut;
